import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, Copy, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";

export default function ShareLinksManager({ defaultYear, defaultStart, defaultEnd, mode }) {
  const [links, setLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    label: "",
    year: defaultYear || new Date().getFullYear(),
    expires_days: 90,
  });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/share-links");
      setLinks(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setForm((f) => ({ ...f, year: defaultYear || f.year }));
  }, [defaultYear]);

  const buildPublicUrl = (token) => `${window.location.origin}/share/${token}`;

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    const payload = {
      label: form.label || null,
      expires_days: parseInt(form.expires_days, 10) || 0,
    };
    if (mode === "range") {
      payload.start = defaultStart;
      payload.end = defaultEnd;
    } else {
      payload.year = parseInt(form.year, 10);
    }
    try {
      const res = await api.post("/share-links", payload);
      toast.success("Share link created");
      setOpen(false);
      setForm({ label: "", year: defaultYear || new Date().getFullYear(), expires_days: 90 });
      load();
      // Auto-copy the new link
      try {
        await navigator.clipboard.writeText(buildPublicUrl(res.data.token));
        toast.success("Copied link to clipboard");
      } catch (err) {
        console.warn("Clipboard write failed:", err);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(buildPublicUrl(token));
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this share link? Your CPA won't be able to access it anymore.")) return;
    try {
      await api.delete(`/share-links/${id}`);
      toast.success("Link revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const activeLinks = links.filter((l) => !l.revoked);

  return (
    <div className="border border-neutral-200 p-6" data-testid="share-links-manager">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">CPA Access</div>
          <div className="font-serif text-2xl">Share with your accountant</div>
          <p className="text-sm text-neutral-500 mt-2 max-w-lg">
            Generate a read-only link your CPA can open in any browser. They can view, print, or download the report — no account needed.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="share-create-button">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          New share link
        </Button>
      </div>

      {activeLinks.length === 0 ? (
        <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 p-6 text-center">
          No active share links yet.
        </div>
      ) : (
        <div className="border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="text-left font-sans font-medium uppercase tracking-[0.15em] text-[10px] px-3 py-2 text-neutral-700">Label</th>
                <th className="text-left font-sans font-medium uppercase tracking-[0.15em] text-[10px] px-3 py-2 text-neutral-700">Period</th>
                <th className="text-left font-sans font-medium uppercase tracking-[0.15em] text-[10px] px-3 py-2 text-neutral-700">Expires</th>
                <th className="text-left font-sans font-medium uppercase tracking-[0.15em] text-[10px] px-3 py-2 text-neutral-700">Views</th>
                <th className="text-left font-sans font-medium uppercase tracking-[0.15em] text-[10px] px-3 py-2 text-neutral-700">Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {activeLinks.map((l) => (
                <tr key={l.id} className="border-t border-neutral-200 row-hover" data-testid={`share-row-${l.id}`}>
                  <td className="px-3 py-2">{l.label}</td>
                  <td className="px-3 py-2 font-mono text-xs text-neutral-600">
                    {l.year ? `Year ${l.year}` : `${(l.start || "").slice(0, 10)} → ${(l.end || "").slice(0, 10)}`}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-neutral-600">
                    {l.expires_at ? formatDate(l.expires_at) : "Never"}
                  </td>
                  <td className="px-3 py-2 font-mono">{l.view_count}</td>
                  <td className="px-3 py-2 font-mono text-xs text-neutral-600">{formatDate(l.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => copyLink(l.token)} className="text-neutral-700 hover:text-neutral-900 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.15em]" data-testid={`share-copy-${l.id}`}>
                        <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Copy
                      </button>
                      <a href={buildPublicUrl(l.token)} target="_blank" rel="noreferrer" className="text-neutral-700 hover:text-neutral-900 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.15em]" data-testid={`share-open-${l.id}`}>
                        <Link className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Open
                      </a>
                      <button onClick={() => revoke(l.id)} className="text-neutral-500 hover:text-red-700" data-testid={`share-revoke-${l.id}`}>
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-neutral-300 max-w-md" data-testid="share-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Create CPA share link</DialogTitle>
            <DialogDescription className="text-neutral-500 text-sm">
              Anyone with the link will be able to view and download this report.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Label (optional)</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. CPA - FY2026"
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900" data-testid="share-label-input" />
            </div>
            {mode === "year" ? (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Tax Year</Label>
                <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })}
                  className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono" data-testid="share-year-input" />
              </div>
            ) : (
              <div className="text-sm text-neutral-500">
                Period: <span className="font-mono text-neutral-800">{defaultStart} → {defaultEnd}</span>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Expires in</Label>
              <Select value={String(form.expires_days)} onValueChange={(v) => setForm({ ...form, expires_days: v })}>
                <SelectTrigger className="mt-2 rounded-none border-neutral-300 focus:ring-0" data-testid="share-expires-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="7" className="rounded-none">7 days</SelectItem>
                  <SelectItem value="30" className="rounded-none">30 days</SelectItem>
                  <SelectItem value="90" className="rounded-none">90 days</SelectItem>
                  <SelectItem value="365" className="rounded-none">1 year</SelectItem>
                  <SelectItem value="0" className="rounded-none">Never expires</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-neutral-300" data-testid="share-cancel-button">
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="share-submit-button">
                {creating ? "Creating…" : "Create link"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
